import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from './index';
import apiClient from '../services/apiClient';

// ── Types ────────────────────────────────────────────────────────────

interface UserPreferencesResponse {
  user_id: number;
  use_custom_template_by_default: boolean;
  has_custom_prompt_override: boolean;
  report_length: string;
  impression_style: string;
  rec_style: string;
  bold_positives: boolean;
  max_impression_points: number;
  active_profile_id?: number;
}

interface CustomPromptResponse {
  effective_prompt: string;
  is_default: boolean;
}

interface UserPreferencesState {
  /** Whether custom template mode is enabled by default */
  useCustomTemplateByDefault: boolean;
  /** Whether admin has set a custom prompt override */
  hasCustomPromptOverride: boolean;
  /** The effective predefined prompt (system default or admin override) */
  effectivePredefinedPrompt: string;
  /** Whether dark mode is enabled */
  darkMode: boolean;
  /** Phase 1 RXF reporting style preferences */
  reportLength: string;
  impressionStyle: string;
  recStyle: string;
  boldPositives: boolean;
  maxImpressionPoints: number;
  activeProfileId?: number;
  /** Loading states */
  loading: boolean;
  saving: boolean;
  promptLoading: boolean;
  promptSaving: boolean;
  reportingPrefsSaving: boolean;
  /** Error state */
  error: string | null;
}

// ── Initial State ───────────────────────────────────────────────────

const initialState: UserPreferencesState = {
  useCustomTemplateByDefault: false,
  hasCustomPromptOverride: false,
  effectivePredefinedPrompt: '',
  darkMode: false,
  reportLength: 'detailed',
  impressionStyle: 'numbered_synthesis',
  recStyle: 'standard',
  boldPositives: false,
  maxImpressionPoints: 0,
  activeProfileId: undefined,
  loading: false,
  saving: false,
  promptLoading: false,
  promptSaving: false,
  reportingPrefsSaving: false,
  error: null,
};

// ── Async Thunks ────────────────────────────────────────────────────

export const fetchUserPreferences = createAsyncThunk(
  'userPreferences/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<UserPreferencesResponse>('/api/v1/user-preferences');
      return response.data;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      return rejectWithValue(err.response?.data?.detail || 'Failed to load preferences');
    }
  }
);

export const updateTogglePreference = createAsyncThunk(
  'userPreferences/updateToggle',
  async (useCustomTemplateByDefault: boolean, { rejectWithValue }) => {
    try {
      const response = await apiClient.put<UserPreferencesResponse>('/api/v1/user-preferences', {
        use_custom_template_by_default: useCustomTemplateByDefault,
      });
      return response.data;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      return rejectWithValue(err.response?.data?.detail || 'Failed to update preference');
    }
  }
);

export const fetchEffectivePrompt = createAsyncThunk(
  'userPreferences/fetchPrompt',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<CustomPromptResponse>('/api/v1/user-preferences/custom-prompt');
      return response.data;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      return rejectWithValue(err.response?.data?.detail || 'Failed to load prompt');
    }
  }
);

export const updateReportingPreferences = createAsyncThunk(
  'userPreferences/updateReporting',
  async (
    prefs: {
      report_length?: string;
      impression_style?: string;
      rec_style?: string;
      bold_positives?: boolean;
      max_impression_points?: number;
      active_profile_id?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.put<UserPreferencesResponse>(
        '/api/v1/user-preferences/reporting',
        prefs
      );
      return response.data;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      return rejectWithValue(err.response?.data?.detail || 'Failed to update reporting preferences');
    }
  }
);

export const updateCustomPrompt = createAsyncThunk(
  'userPreferences/updatePrompt',
  async (customPrompt: string | null, { rejectWithValue }) => {
    try {
      const response = await apiClient.put<CustomPromptResponse>('/api/v1/user-preferences/custom-prompt', {
        custom_prompt: customPrompt,
      });
      return response.data;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      return rejectWithValue(err.response?.data?.detail || 'Failed to update prompt');
    }
  }
);

// ── Slice ───────────────────────────────────────────────────────────

const userPreferencesSlice = createSlice({
  name: 'userPreferences',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    resetPreferences(state) {
      state.useCustomTemplateByDefault = false;
      state.hasCustomPromptOverride = false;
      state.effectivePredefinedPrompt = '';
      state.darkMode = false;
      state.error = null;
    },
    toggleDarkMode(state) {
      state.darkMode = !state.darkMode;
    },
  },
  extraReducers: (builder) => {
    // Fetch preferences
    builder.addCase(fetchUserPreferences.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchUserPreferences.fulfilled, (state, action) => {
      state.loading = false;
      state.useCustomTemplateByDefault = action.payload.use_custom_template_by_default;
      state.hasCustomPromptOverride = action.payload.has_custom_prompt_override;
      state.reportLength = action.payload.report_length || 'detailed';
      state.impressionStyle = action.payload.impression_style || 'numbered_synthesis';
      state.recStyle = action.payload.rec_style || 'standard';
      state.boldPositives = action.payload.bold_positives || false;
      state.maxImpressionPoints = action.payload.max_impression_points || 0;
      state.activeProfileId = action.payload.active_profile_id;
    });
    builder.addCase(fetchUserPreferences.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as string) || 'Unknown error';
    });

    // Update toggle
    builder.addCase(updateTogglePreference.pending, (state) => {
      state.saving = true;
      state.error = null;
    });
    builder.addCase(updateTogglePreference.fulfilled, (state, action) => {
      state.saving = false;
      state.useCustomTemplateByDefault = action.payload.use_custom_template_by_default;
    });

    // Update reporting preferences
    builder.addCase(updateReportingPreferences.pending, (state) => {
      state.reportingPrefsSaving = true;
      state.error = null;
    });
    builder.addCase(updateReportingPreferences.fulfilled, (state, action) => {
      state.reportingPrefsSaving = false;
      state.reportLength = action.payload.report_length || 'detailed';
      state.impressionStyle = action.payload.impression_style || 'numbered_synthesis';
      state.recStyle = action.payload.rec_style || 'standard';
      state.boldPositives = action.payload.bold_positives || false;
      state.maxImpressionPoints = action.payload.max_impression_points || 0;
      state.activeProfileId = action.payload.active_profile_id;
    });
    builder.addCase(updateReportingPreferences.rejected, (state, action) => {
      state.reportingPrefsSaving = false;
      state.error = (action.payload as string) || 'Unknown error';
    });
    builder.addCase(updateTogglePreference.rejected, (state, action) => {
      state.saving = false;
      state.error = (action.payload as string) || 'Unknown error';
    });

    // Fetch effective prompt
    builder.addCase(fetchEffectivePrompt.pending, (state) => {
      state.promptLoading = true;
      state.error = null;
    });
    builder.addCase(fetchEffectivePrompt.fulfilled, (state, action) => {
      state.promptLoading = false;
      state.effectivePredefinedPrompt = action.payload.effective_prompt;
    });
    builder.addCase(fetchEffectivePrompt.rejected, (state, action) => {
      state.promptLoading = false;
      state.error = (action.payload as string) || 'Unknown error';
    });

    // Update custom prompt
    builder.addCase(updateCustomPrompt.pending, (state) => {
      state.promptSaving = true;
      state.error = null;
    });
    builder.addCase(updateCustomPrompt.fulfilled, (state, action) => {
      state.promptSaving = false;
      state.effectivePredefinedPrompt = action.payload.effective_prompt;
      state.hasCustomPromptOverride = !action.payload.is_default;
    });
    builder.addCase(updateCustomPrompt.rejected, (state, action) => {
      state.promptSaving = false;
      state.error = (action.payload as string) || 'Unknown error';
    });
  },
});

// ── Actions ─────────────────────────────────────────────────────────

export const { clearError, resetPreferences, toggleDarkMode } = userPreferencesSlice.actions;

// ── Selectors ───────────────────────────────────────────────────────

export const selectUserPreferences = (state: RootState) => state.userPreferences;
export const selectUseCustomTemplateByDefault = (state: RootState) =>
  state.userPreferences.useCustomTemplateByDefault;
export const selectEffectivePredefinedPrompt = (state: RootState) =>
  state.userPreferences.effectivePredefinedPrompt;
export const selectHasCustomPromptOverride = (state: RootState) =>
  state.userPreferences.hasCustomPromptOverride;
export const selectUserPreferencesLoading = (state: RootState) =>
  state.userPreferences.loading;
export const selectUserPreferencesError = (state: RootState) =>
  state.userPreferences.error;
export const selectDarkMode = (state: RootState) =>
  state.userPreferences.darkMode;
export const selectReportingPrefs = (state: RootState) => ({
  reportLength: state.userPreferences.reportLength,
  impressionStyle: state.userPreferences.impressionStyle,
  recStyle: state.userPreferences.recStyle,
  boldPositives: state.userPreferences.boldPositives,
  maxImpressionPoints: state.userPreferences.maxImpressionPoints,
  activeProfileId: state.userPreferences.activeProfileId,
  saving: state.userPreferences.reportingPrefsSaving,
});

export default userPreferencesSlice.reducer;
